import React, { useState, useEffect } from 'react';
import { 
  Crown, Trophy, Star, DollarSign, TrendingUp, 
  Calendar, Users, Target, BarChart3, Award,
  ChevronRight, Zap, Medal, Activity, ChevronDown, ChevronUp
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const PositionRingOfFame = ({ draftData, loading, error }) => {
  const [positionData, setPositionData] = useState({});
  const [expandedPositions, setExpandedPositions] = useState({});
  // Separate tooltip state for each position
  const [tooltipStates, setTooltipStates] = useState({});

  // Utility function to get player headshot with fallbacks
  const getPlayerHeadshot = (playerName) => {
    if (!playerName) return null;
    
    // Clean player name for checking
    const cleanName = playerName.trim();
    
    // Check for specific players with local images
    if (cleanName === "Rob Gronkowski" || cleanName === "Gronkowski" || cleanName === "Rob Gronkoski") {
      return ['/gronk.png'];
    }
    
    if (cleanName === "Saquon Barkley" || cleanName === "Barkley" || cleanName === "Saquon") {
      return ['/saquon.png'];
    }
    
    if (cleanName === "Aaron Rodgers" || cleanName === "Rodgers" || cleanName === "A Rodgers") {
      return ['/rodgers.png'];
    }
    
    if (cleanName === "Justin Tucker" || cleanName === "Tucker" || cleanName === "J Tucker") {
      return ['/tucker.png'];
    }
    
    if (cleanName === "SF D" || cleanName === "San Francisco" || cleanName === "49ers" || cleanName === "San Francisco D" || cleanName === "49ers D") {
      return ['/49ers.png'];
    }
    
    // Clean player name for URL formatting for other players
    const nameForUrl = cleanName.toLowerCase().replace(/[^a-z\s]/g, '').replace(/\s+/g, '-');
    
    // Multiple fallback sources for other players
    const sources = [
      // ESPN (most reliable for current players)
      `https://a.espncdn.com/i/headshots/nfl/players/full/${nameForUrl}.png`,
      // NFL.com alternative
      `https://static.www.nfl.com/image/private/t_headshot_desktop/nfl/players/${nameForUrl}`,
      // Sports Reference (good for historical players)
      `https://www.pro-football-reference.com/req/202301051/images/headshots/${nameForUrl}_2023.jpg`,
      // Generic fallback
      `https://via.placeholder.com/120x120/e5e7eb/6b7280?text=${playerName.split(' ').map(n => n[0]).join('')}`
    ];
    
    return sources;
  };

  // Component to handle image loading with fallbacks - Enhanced stability
  const PlayerHeadshot = React.memo(({ playerName, className = "", size = 80 }) => {
    const [imageState, setImageState] = useState({
      currentSrcIndex: 0,
      isLoading: true,
      hasError: false,
      loadedSrc: null,
      hasSuccessfullyLoaded: false
    });
    
    const sources = React.useMemo(() => getPlayerHeadshot(playerName), [playerName]);
    const stablePlayerName = React.useMemo(() => playerName?.trim(), [playerName]);
    
    // Only reset when playerName actually changes
    useEffect(() => {
      setImageState({
        currentSrcIndex: 0,
        isLoading: true,
        hasError: false,
        loadedSrc: null,
        hasSuccessfullyLoaded: false
      });
    }, [stablePlayerName]);
    
    const handleImageError = React.useCallback(() => {
      setImageState(prev => {
        if (prev.currentSrcIndex < sources.length - 1) {
          return {
            ...prev,
            currentSrcIndex: prev.currentSrcIndex + 1,
            loadedSrc: sources[prev.currentSrcIndex + 1]
          };
        } else {
          return {
            ...prev,
            hasError: true,
            isLoading: false,
            loadedSrc: null
          };
        }
      });
    }, [sources]);
    
    const handleImageLoad = React.useCallback(() => {
      setImageState(prev => ({
        ...prev,
        isLoading: false,
        hasError: false,
        hasSuccessfullyLoaded: true
      }));
    }, []);
    
    // Set initial image source
    useEffect(() => {
      if (sources && sources[imageState.currentSrcIndex] && !imageState.loadedSrc) {
        setImageState(prev => ({
          ...prev,
          loadedSrc: sources[prev.currentSrcIndex]
        }));
      }
    }, [sources, imageState.currentSrcIndex, imageState.loadedSrc]);
    
    // Special handling for players with local images
    const cleanName = stablePlayerName;
    const isGronk = cleanName === "Rob Gronkowski" || cleanName === "Gronkowski" || cleanName === "Rob Gronkoski";
    const isSaquon = cleanName === "Saquon Barkley" || cleanName === "Barkley" || cleanName === "Saquon";
    const isRodgers = cleanName === "Aaron Rodgers" || cleanName === "Rodgers" || cleanName === "A Rodgers";
    const isTucker = cleanName === "Justin Tucker" || cleanName === "Tucker" || cleanName === "J Tucker";
    const is49ers = cleanName === "SF D" || cleanName === "San Francisco" || cleanName === "49ers" || cleanName === "San Francisco D" || cleanName === "49ers D";
    const isSpecialPlayer = isGronk || isSaquon || isRodgers || isTucker || is49ers;
    
    if (!sources || imageState.hasError) {
      // Fallback to icon if all images fail
      return (
        <div className={`${className} flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-full`} 
             style={{ width: size, height: size }}>
          <Users className="h-8 w-8 text-gray-400" />
        </div>
      );
    }
    
    return (
      <div className={`${className} relative`} style={{ width: size, height: size }}>
        {/* Loading state - only show if we haven't successfully loaded yet */}
        {imageState.isLoading && !imageState.hasSuccessfullyLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-full z-10">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-gray-600"></div>
          </div>
        )}
        
        {/* Image - always rendered once we have a source */}
        {imageState.loadedSrc && (
          <img
            key={`${stablePlayerName}-${imageState.currentSrcIndex}`} // Stable key
            src={imageState.loadedSrc}
            alt={`${playerName} headshot`}
            className={`w-full h-full object-cover rounded-full shadow-lg transition-opacity duration-300 ${
              imageState.isLoading && !imageState.hasSuccessfullyLoaded ? 'opacity-0' : 'opacity-100'
            } ${isSpecialPlayer ? 'ring-4 ring-yellow-400 ring-opacity-75' : ''}`}
            onError={handleImageError}
            onLoad={handleImageLoad}
            loading="lazy"
            style={{ 
              // Ensure image stays visible once loaded
              visibility: imageState.hasSuccessfullyLoaded ? 'visible' : 'hidden'
            }}
          />
        )}
        
        {/* Special player indicator - only show when image is successfully loaded */}
        {isSpecialPlayer && imageState.hasSuccessfullyLoaded && !imageState.hasError && (
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg">
            <span className="text-xs">
              {isGronk ? '🏆' : 
               isSaquon ? '⭐' : 
               isRodgers ? '👑' : 
               isTucker ? '🦵' : 
               is49ers ? '🛡️' : '🏆'}
            </span>
          </div>
        )}
      </div>
    );
  });

  // Position configuration with colors and icons
  const positionConfig = {
    QB: { 
      color: '#ef4444', 
      bgColor: 'from-red-50 via-red-25 to-white', 
      borderColor: 'border-red-200',
      accentColor: 'bg-red-500',
      icon: '🏈',
      name: 'Quarterback'
    },
    RB: { 
      color: '#22c55e', 
      bgColor: 'from-green-50 via-green-25 to-white', 
      borderColor: 'border-green-200',
      accentColor: 'bg-green-500',
      icon: '🏃‍♂️',
      name: 'Running Back'
    },
    TE: { 
      color: '#eab308', 
      bgColor: 'from-yellow-50 via-yellow-25 to-white', 
      borderColor: 'border-yellow-200',
      accentColor: 'bg-yellow-500',
      icon: '🙌',
      name: 'Tight End'
    },
    K: { 
      color: '#a855f7', 
      bgColor: 'from-purple-50 via-purple-25 to-white', 
      borderColor: 'border-purple-200',
      accentColor: 'bg-purple-500',
      icon: '🦵',
      name: 'Kicker'
    },
    DEF: { 
      color: '#6b7280', 
      bgColor: 'from-gray-50 via-gray-25 to-white', 
      borderColor: 'border-gray-200',
      accentColor: 'bg-gray-500',
      icon: '🛡️',
      name: 'Defense'
    }
  };

  // Process data to find highest-paid player for each position
  useEffect(() => {
    if (!draftData || draftData.length === 0) return;

    const positions = ['QB', 'RB', 'TE', 'K', 'DEF'];
    const newPositionData = {};

    positions.forEach(position => {
      // Filter data for this position
      const positionRecords = draftData.filter(record => record.Position === position);
      
      if (positionRecords.length === 0) return;

      // Group by player
      const playerInvestments = positionRecords.reduce((acc, record) => {
        const player = record.Player?.trim() || '';
        if (!acc[player]) {
          acc[player] = {
            totalCost: 0,
            draftCount: 0,
            records: []
          };
        }
        acc[player].totalCost += record.Cost || 0;
        acc[player].draftCount += 1;
        acc[player].records.push(record);
        return acc;
      }, {});

      // Find highest-paid player for this position
      const topPlayer = Object.entries(playerInvestments)
        .sort(([,a], [,b]) => b.totalCost - a.totalCost)[0];

      if (!topPlayer) return;

      const [playerName, stats] = topPlayer;

      // Calculate manager investments for this player
      const managerData = stats.records.reduce((acc, record) => {
        const manager = record.Manager;
        const investment = record.Investment;
        
        const existing = acc.find(item => item.manager === manager);
        if (existing) {
          existing.Drafted += investment === 'Drafted' ? (record.Cost || 0) : 0;
          existing.Keeper += investment === 'Keeper' ? (record.Cost || 0) : 0;
          existing.total += record.Cost || 0;
        } else {
          acc.push({
            manager: manager,
            Drafted: investment === 'Drafted' ? (record.Cost || 0) : 0,
            Keeper: investment === 'Keeper' ? (record.Cost || 0) : 0,
            total: record.Cost || 0
          });
        }
        return acc;
      }, []).sort((a, b) => b.total - a.total);

      // Calculate yearly data for scatter plot
      const featuredPlayerYears = stats.records.map(r => r.Year);
      const playerMinYear = Math.min(...featuredPlayerYears);
      const playerMaxYear = Math.max(...featuredPlayerYears);
      
      // Create dots for the scatter plot
      const allPositionDots = positionRecords
        .filter(record => record.Year >= playerMinYear && record.Year <= playerMaxYear)
        .map((record, index) => {
          const jitterRange = 0.3;
          const jitter = (Math.random() - 0.5) * jitterRange * 2;
          const isHighlighted = (record.Player?.trim() || '') === playerName;
          
          return {
            year: record.Year + (isHighlighted ? 0 : jitter),
            amount: record.Cost || 0,
            player: record.Player?.trim() || '',
            manager: record.Manager,
            investment: record.Investment,
            isHighlighted: isHighlighted,
            id: `${record.Player?.trim()}-${record.Year}-${record.Manager}-${index}`
          };
        });

      newPositionData[position] = {
        playerName,
        totalCost: stats.totalCost,
        draftCount: stats.draftCount,
        years: [...new Set(stats.records.map(r => r.Year))].length,
        managers: [...new Set(stats.records.map(r => r.Manager))].length,
        yearRange: { min: playerMinYear, max: playerMaxYear },
        managerData,
        allDots: allPositionDots
      };
    });

    setPositionData(newPositionData);
  }, [draftData]);

  const togglePosition = (position) => {
    setExpandedPositions(prev => ({
      ...prev,
      [position]: !prev[position]
    }));
  };

  const renderScatterPlot = (position, data) => {
    const tooltipState = tooltipStates[position] || { hoveredDot: null, mousePosition: { x: 0, y: 0 }, showTooltip: false };
    
    const setTooltipState = (newState) => {
      setTooltipStates(prev => ({
        ...prev,
        [position]: { ...tooltipState, ...newState }
      }));
    };

    return (
      <div className="relative">
        <div className="h-48 sm:h-56 lg:h-56 relative bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-sm">
          {/* Clean, minimal background for chart readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50/30"></div>
          
          {/* Chart Container - Responsive padding */}
          <div className="absolute z-10" style={{ top: 16, bottom: 24, left: 36, right: 16 }}>
            {/* Y-axis labels - Smaller on mobile */}
            <div className="absolute -left-8 inset-y-0 flex flex-col justify-between text-xs text-gray-500 font-medium">
              <span>80</span>
              <span>60</span>
              <span>40</span>
              <span>20</span>
              <span>0</span>
            </div>
            
            {/* X-axis labels - Responsive */}
            <div className="absolute -bottom-5 inset-x-0 flex justify-between text-xs text-gray-500 font-medium">
              {Array.from(
                { length: data.yearRange.max - data.yearRange.min + 1 }, 
                (_, i) => {
                  const year = data.yearRange.min + i;
                  // On mobile, show every other year if range is large
                  const yearSpan = data.yearRange.max - data.yearRange.min;
                  const showYear = yearSpan <= 6 || i % 2 === 0 || window.innerWidth >= 640;
                  return (
                    <span key={i} className={showYear ? '' : 'hidden sm:inline'}>
                      {year}
                    </span>
                  );
                }
              )}
            </div>
            
            {/* High-resolution chart area */}
            <div 
              className="absolute inset-0 w-full h-full"
              onMouseLeave={() => {
                setTooltipState({ showTooltip: false, hoveredDot: null });
              }}
            >
              {/* Grid lines */}
              <svg className="absolute inset-0 w-full h-full">
                {[0, 20, 40, 60, 80].map(value => {
                  const yPercent = 100 - (value / 80) * 100;
                  return (
                    <line
                      key={value}
                      x1="0"
                      y1={`${yPercent}%`}
                      x2="100%"
                      y2={`${yPercent}%`}
                      stroke="#f1f5f9"
                      strokeDasharray="2,3"
                      strokeWidth="1"
                    />
                  );
                })}
                
                {/* Vertical grid lines */}
                {Array.from(
                  { length: data.yearRange.max - data.yearRange.min + 1 }, 
                  (_, i) => {
                    const xPercent = (i / (data.yearRange.max - data.yearRange.min)) * 100;
                    return (
                      <line
                        key={i}
                        x1={`${xPercent}%`}
                        y1="0"
                        x2={`${xPercent}%`}
                        y2="100%"
                        stroke="#f8fafc"
                        strokeDasharray="2,3"
                        strokeWidth="1"
                      />
                    );
                  }
                )}
              </svg>
              
              {/* Data points - Mobile responsive sizes */}
              {data.allDots.map((dot, index) => {
                const yearSpan = data.yearRange.max - data.yearRange.min;
                const xPercent = yearSpan > 0 ? ((dot.year - data.yearRange.min) / yearSpan) * 100 : 50;
                const yPercent = 100 - ((dot.amount / 80) * 100);
                
                const handleMouseEnter = (e) => {
                  const chartContainer = e.currentTarget.closest('.relative');
                  const chartRect = chartContainer.getBoundingClientRect();
                  const dotRect = e.currentTarget.getBoundingClientRect();
                  
                  setTooltipState({
                    hoveredDot: dot,
                    mousePosition: {
                      x: dotRect.left + dotRect.width / 2,
                      y: dotRect.top
                    },
                    showTooltip: true
                  });
                };

                const handleMouseLeave = () => {
                  setTooltipState({
                    showTooltip: false,
                    hoveredDot: null
                  });
                };

                const isCurrentlyHovered = tooltipState.hoveredDot?.id === dot.id;
                const isHighlighted = dot.isHighlighted;
                const isMobile = window.innerWidth < 640;

                return (
                  <div
                    key={`${position}-dot-${index}`}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-150"
                    style={{
                      left: `${xPercent}%`,
                      top: `${yPercent}%`,
                      width: isHighlighted 
                        ? (isMobile ? '10px' : '12px') 
                        : (isCurrentlyHovered ? (isMobile ? '8px' : '10px') : (isMobile ? '5px' : '6px')),
                      height: isHighlighted 
                        ? (isMobile ? '10px' : '12px') 
                        : (isCurrentlyHovered ? (isMobile ? '8px' : '10px') : (isMobile ? '5px' : '6px')),
                      borderRadius: '50%',
                      backgroundColor: isHighlighted 
                        ? positionConfig[position].color 
                        : (isCurrentlyHovered ? '#8b5cf6' : '#e2e8f0'),
                      border: isHighlighted 
                        ? '2px solid white' 
                        : (isCurrentlyHovered ? '2px solid #7c3aed' : '1px solid #cbd5e1'),
                      boxShadow: isHighlighted 
                        ? `0 4px 12px ${positionConfig[position].color}40` 
                        : (isCurrentlyHovered ? '0 4px 12px rgba(139, 92, 246, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)'),
                      zIndex: isHighlighted ? 30 : (isCurrentlyHovered ? 20 : 10)
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  />
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Legend - Mobile responsive */}
        <div className="flex justify-center mt-4 sm:mt-6 space-x-4 sm:space-x-8">
          <div className="flex items-center bg-gray-50 rounded-full px-3 sm:px-4 py-2 shadow-sm">
            <div className="w-2 h-2 sm:w-3 sm:h-3 bg-gray-400 rounded-full mr-2 sm:mr-3"></div>
            <span className="text-xs sm:text-sm text-gray-700 font-semibold">All {position}s</span>
          </div>
          <div className="flex items-center rounded-full px-3 sm:px-4 py-2 shadow-sm" style={{ 
            backgroundColor: `${positionConfig[position].color}15`
          }}>
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3" style={{ 
              backgroundColor: positionConfig[position].color
            }}></div>
            <span className="text-xs sm:text-sm font-semibold" style={{ color: positionConfig[position].color }}>{data.playerName}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderBarChart = (position, data) => {
    return (
      <div className="h-40 sm:h-48 lg:h-48 bg-white rounded-lg">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.managerData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              dataKey="manager" 
              stroke="#6b7280"
              fontSize={window.innerWidth < 640 ? 8 : 9}
              tickLine={false}
              axisLine={false}
              interval={0}
              angle={window.innerWidth < 640 ? -45 : 0}
              textAnchor={window.innerWidth < 640 ? "end" : "middle"}
              height={window.innerWidth < 640 ? 50 : 30}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={window.innerWidth < 640 ? 8 : 9}
              tickLine={false}
              axisLine={false}
              width={window.innerWidth < 640 ? 25 : 30}
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const total = payload.reduce((sum, entry) => sum + entry.value, 0);
                  const playerRecords = draftData.filter(record => 
                    record.Manager === label && 
                    record.Player?.trim() === data.playerName?.trim() &&
                    record.Position === position
                  );
                  const years = [...new Set(playerRecords.map(r => r.Year))].sort();
                  const yearText = years.length > 2 ? `${years[0]}-${years[years.length - 1]}` : years.join(', ');
                  
                  const isMobile = window.innerWidth < 640;
                  
                  return (
                    <div style={{
                      backgroundColor: 'rgba(15, 23, 42, 0.98)',
                      border: '1px solid rgba(71, 85, 105, 0.4)',
                      borderRadius: isMobile ? '16px' : '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      fontSize: isMobile ? '11px' : '12px',
                      padding: isMobile ? '14px' : '12px',
                      minWidth: isMobile ? '160px' : '180px',
                      backdropFilter: 'blur(16px)',
                    }}>
                      <div style={{
                        color: '#fbbf24',
                        fontWeight: '700',
                        fontSize: isMobile ? '10px' : '11px',
                        marginBottom: '8px',
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid rgba(251, 191, 36, 0.3)',
                        paddingBottom: '6px'
                      }}>
                        {label} → {data.playerName}
                      </div>
                      
                      <div style={{
                        marginBottom: '8px',
                        padding: '6px 8px',
                        backgroundColor: 'rgba(6, 182, 212, 0.15)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: '#94a3b8', fontSize: isMobile ? '8px' : '9px', marginBottom: '2px' }}>YEARS</div>
                        <div style={{ color: '#e2e8f0', fontSize: isMobile ? '10px' : '11px', fontWeight: '600' }}>{yearText}</div>
                      </div>

                      {payload.map((entry, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '4px',
                          padding: '4px 8px',
                          backgroundColor: entry.dataKey === 'Drafted' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                          borderRadius: '4px'
                        }}>
                          <span style={{ fontSize: isMobile ? '9px' : '10px' }}>{entry.dataKey}:</span>
                          <span style={{ fontWeight: '600', fontSize: isMobile ? '9px' : '10px' }}>${entry.value}</span>
                        </div>
                      ))}
                      
                      <div style={{
                        borderTop: '1px solid rgba(71, 85, 105, 0.4)',
                        paddingTop: '6px',
                        marginTop: '6px',
                        textAlign: 'center'
                      }}>
                        <span style={{ color: '#06b6d4', fontWeight: '700', fontSize: isMobile ? '11px' : '12px' }}>
                          Total: ${total}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="Drafted" stackId="a" fill="#8b5cf6" />
            <Bar dataKey="Keeper" stackId="a" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-3xl p-8">
        <div className="animate-pulse space-y-6">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="h-20 bg-purple-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || Object.keys(positionData).length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-3xl p-8">
        <h3 className="text-lg font-medium text-red-800 mb-2">Unable to Load Position Data</h3>
        <p className="text-red-600">{error || 'No position data available'}</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 rounded-3xl shadow-2xl border border-white/50 p-4 sm:p-6 lg:p-8 overflow-hidden">
      {/* Header - Mobile optimized */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mb-3 sm:mb-4 shadow-xl">
          <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
          Position Ring of Fame
        </h2>
        <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4 sm:px-0">
          Honoring the highest-paid player at each position in Beasts of Bellevue history
        </p>
      </div>

      {/* Position Cards */}
      <div className="space-y-4 sm:space-y-6">
        {Object.entries(positionData).map(([position, data]) => {
          const config = positionConfig[position];
          const isExpanded = expandedPositions[position];
          
          return (
            <div key={position} className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-700 hover:shadow-3xl hover:scale-[1.02] relative group"
                 style={{
                   background: `linear-gradient(135deg, ${config.color}12, ${config.color}06, rgba(255,255,255,0.08))`,
                   boxShadow: `0 25px 50px -12px ${config.color}15, 0 8px 32px ${config.color}08`
                 }}>
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br opacity-40 group-hover:opacity-60 transition-opacity duration-700" style={{
                background: `linear-gradient(135deg, ${config.color}15, transparent, ${config.color}08)`
              }}></div>
              
              {/* Flowing mesh pattern overlay */}
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, ${config.color} 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }}></div>

              {/* Header - Mobile Layout */}
              <div 
                className="p-4 sm:p-6 lg:p-8 cursor-pointer transition-all duration-500 relative group/header"
                onClick={() => togglePosition(position)}
              >
                {/* Dynamic gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover/header:opacity-100 transition-all duration-500 rounded-t-2xl sm:rounded-t-3xl" style={{
                  background: `linear-gradient(90deg, ${config.color}08, transparent, ${config.color}08)`
                }}></div>
                
                {/* Mobile Layout */}
                <div className="block lg:hidden relative z-10">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      {/* Player Headshot - Smaller on mobile */}
                      <PlayerHeadshot 
                        playerName={data.playerName}
                        size={60}
                        className="group-hover/icon:scale-105 transition-transform duration-500"
                      />
                      
                      <div>
                        <h3 className="text-lg sm:text-xl font-black relative">
                          <span className="bg-gradient-to-r bg-clip-text text-transparent drop-shadow-sm" style={{
                            backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}dd, ${config.color})`
                          }}>
                            {data.playerName}
                          </span>
                        </h3>
                        <div className="text-sm text-gray-600 font-medium">{config.name}</div>
                      </div>
                    </div>

                    {/* Mobile Stats & Toggle */}
                    <div className="flex items-center space-x-3">
                      <div className="text-right backdrop-blur-3xl rounded-lg p-3 shadow-lg" style={{
                        background: `linear-gradient(135deg, ${config.color}12, rgba(255,255,255,0.08), ${config.color}08)`
                      }}>
                        <div className="text-lg sm:text-xl font-black bg-gradient-to-r bg-clip-text text-transparent" style={{
                          backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}cc, ${config.color}dd)`
                        }}>
                          ${data.totalCost}
                        </div>
                        <div className="text-xs text-gray-600 font-bold">{data.draftCount} drafts</div>
                      </div>
                      
                      <div className="backdrop-blur-2xl rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110 cursor-pointer" style={{
                        background: `radial-gradient(circle, ${config.color}15, rgba(255,255,255,0.1))`
                      }}>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5" style={{ color: config.color }} />
                        ) : (
                          <ChevronDown className="h-5 w-5" style={{ color: config.color }} />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Description */}
                  <p className="text-sm text-gray-700 leading-relaxed">
                    is the <span className="font-bold text-gray-900">{config.name}</span> who has been paid the most by Beasts of Bellevue managers, earning{' '}
                    <span className="font-black text-lg bg-gradient-to-r bg-clip-text text-transparent" style={{
                      backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}cc, ${config.color})`
                    }}>
                      ${data.totalCost}
                    </span> over <span className="font-bold">{data.draftCount}</span> drafts.
                  </p>
                </div>

                {/* Desktop Layout - Hidden on mobile, unchanged */}
                <div className="hidden lg:flex items-center justify-between relative z-10">
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-4">
                      {/* Player Headshot */}
                      <PlayerHeadshot 
                        playerName={data.playerName}
                        size={80}
                        className="group-hover/icon:scale-105 transition-transform duration-500"
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-2xl lg:text-3xl font-black mb-3 relative">
                        <span className="bg-gradient-to-r bg-clip-text text-transparent drop-shadow-sm" style={{
                          backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}dd, ${config.color})`
                        }}>
                          {data.playerName}
                        </span>
                        {/* Subtle text glow */}
                        <div className="absolute inset-0 blur-sm opacity-30" style={{
                          background: `linear-gradient(135deg, ${config.color}, ${config.color}dd)`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent'
                        }}>
                          {data.playerName}
                        </div>
                      </h3>
                      <p className="text-gray-700 leading-relaxed text-lg font-medium">
                        is the <span className="font-bold text-gray-900">{config.name}</span> who has been paid the most by Beasts of Bellevue managers. 
                        <span className="font-bold text-gray-900"> {data.playerName}</span> has been paid a total of{' '}
                        <span className="font-black text-2xl bg-gradient-to-r bg-clip-text text-transparent relative" style={{
                          backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}cc, ${config.color})`
                        }}>
                          ${data.totalCost}
                          {/* Glow effect for the cost */}
                          <span className="absolute inset-0 blur-sm opacity-40" style={{
                            background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>
                            ${data.totalCost}
                          </span>
                        </span> dollars 
                        over the course of <span className="font-bold text-gray-900">{data.draftCount}</span> fantasy drafts.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    <div className="text-right backdrop-blur-3xl rounded-2xl p-5 shadow-2xl relative overflow-hidden group/stats" style={{
                      background: `linear-gradient(135deg, ${config.color}12, rgba(255,255,255,0.08), ${config.color}08)`,
                      boxShadow: `0 25px 40px -10px ${config.color}25, 0 0 80px ${config.color}15`
                    }}>
                      {/* Flowing particle field */}
                      <div className="absolute inset-0">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-0.5 h-0.5 rounded-full animate-pulse"
                            style={{
                              background: `radial-gradient(circle, ${config.color}, transparent)`,
                              left: `${15 + i * 18}%`,
                              top: `${10 + i * 20}%`,
                              animationDelay: `${i * 0.3}s`,
                              animationDuration: '3s',
                              boxShadow: `0 0 20px ${config.color}60`
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Organic gradient waves */}
                      <div className="absolute inset-0 opacity-[0.04]" style={{
                        background: `repeating-linear-gradient(45deg, ${config.color}, ${config.color} 2px, transparent 2px, transparent 20px)`
                      }}></div>
                      
                      <div className="relative z-10">
                        <div className="text-3xl lg:text-4xl font-black mb-2 bg-gradient-to-r bg-clip-text text-transparent relative" style={{
                          backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}cc, ${config.color}dd)`
                        }}>
                          ${data.totalCost}
                          {/* Enhanced text glow */}
                          <div className="absolute inset-0 blur-xl opacity-60" style={{
                            background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                          }}>
                            ${data.totalCost}
                          </div>
                        </div>
                        <div className="text-sm font-bold text-gray-600">
                          {data.draftCount} drafts • {data.years} years
                        </div>
                      </div>
                    </div>
                    
                    <div className="backdrop-blur-2xl rounded-full p-4 shadow-xl transition-all duration-300 hover:scale-110 cursor-pointer relative overflow-hidden" style={{
                      background: `radial-gradient(circle, ${config.color}15, rgba(255,255,255,0.1))`,
                      boxShadow: `0 15px 30px -5px ${config.color}25`
                    }}>
                      {/* Subtle inner glow */}
                      <div className="absolute inset-0 rounded-full" style={{
                        background: `radial-gradient(circle at center, ${config.color}10, transparent)`
                      }}></div>
                      
                      {isExpanded ? (
                        <ChevronUp className="h-7 w-7 relative z-10" style={{ color: config.color, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                      ) : (
                        <ChevronDown className="h-7 w-7 relative z-10" style={{ color: config.color, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Content - Charts - Mobile optimized */}
              {isExpanded && (
                <div className="backdrop-blur-3xl overflow-hidden relative">
                  {/* Seamless gradient flow */}
                  <div className="absolute inset-0 bg-gradient-to-br" style={{
                    background: `linear-gradient(135deg, ${config.color}06, rgba(255,255,255,0.06), ${config.color}04)`
                  }}></div>
                  
                  {/* Organic flowing elements */}
                  <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10 animate-pulse" style={{
                    background: `radial-gradient(circle, ${config.color}30, transparent)`
                  }}></div>
                  <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-8 animate-pulse" style={{
                    background: `radial-gradient(circle, ${config.color}25, transparent)`,
                    animationDelay: '2s'
                  }}></div>
                  
                  <div className="relative z-10 p-4 sm:p-6 lg:p-10">
                    {/* Mobile: Stacked layout, Desktop: Grid */}
                    <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-1 xl:grid-cols-2 lg:gap-12">
                      {/* Scatter Plot */}
                      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-sm overflow-hidden">
                        <h4 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                          <div className="bg-white p-2 rounded-xl mr-3 sm:mr-4">
                            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                              {data.playerName}'s {config.name} Era
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 font-medium">Investment timeline across seasons</div>
                          </div>
                        </h4>
                        {renderScatterPlot(position, data)}
                      </div>

                      {/* Bar Chart */}
                      <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-sm overflow-hidden">
                        <h4 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                          <div className="bg-white p-2 rounded-xl mr-3 sm:mr-4">
                            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                              Investors in {data.playerName}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 font-medium">Manager investment breakdown</div>
                          </div>
                        </h4>
                        {renderBarChart(position, data)}
                        <div className="flex justify-center mt-4 sm:mt-6 space-x-4 sm:space-x-8">
                          <div className="flex items-center bg-purple-50 rounded-full px-3 sm:px-4 py-2 shadow-sm">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded mr-2 sm:mr-3"></div>
                            <span className="text-xs sm:text-sm text-purple-800 font-semibold">Drafted</span>
                          </div>
                          <div className="flex items-center bg-amber-50 rounded-full px-3 sm:px-4 py-2 shadow-sm">
                            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-amber-500 rounded mr-2 sm:mr-3"></div>
                            <span className="text-xs sm:text-sm text-amber-800 font-semibold">Keeper</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Global Tooltip Container - Mobile optimized positioning */}
      <div style={{ position: 'relative', zIndex: 10000 }}>
        {Object.values(tooltipStates).some(state => state.showTooltip) && (
          <div>
            {Object.entries(tooltipStates).map(([position, tooltipState]) => {
              if (!tooltipState.showTooltip || !tooltipState.hoveredDot) return null;
              
              const hoveredDot = tooltipState.hoveredDot;
              const mousePosition = tooltipState.mousePosition;
              const isHighlighted = hoveredDot.isHighlighted;
              const hoveredYear = Math.round(hoveredDot.year || 0);
              const value = hoveredDot.amount || 0;
              
              const config = positionConfig[position];
              const data = positionData[position];
              
              // Find the featured player's investment in this specific year for comparison
              const featuredThisYear = data.allDots.find(dot => 
                dot.isHighlighted && Math.round(dot.year) === hoveredYear
              );
              
              const featuredAmount = featuredThisYear?.amount || 0;

              // Mobile-responsive tooltip positioning
              const isMobile = window.innerWidth < 640;
              const tooltipWidth = isMobile ? 180 : 220;
              
              const tooltipStyle = {
                position: 'fixed',
                left: Math.min(mousePosition.x, window.innerWidth - tooltipWidth - 10),
                top: Math.max(10, mousePosition.y - (isMobile ? 120 : 100)),
                transform: 'translate(-50%, -100%)',
                backgroundColor: 'rgba(15, 23, 42, 0.98)',
                border: '1px solid rgba(71, 85, 105, 0.4)',
                borderRadius: isMobile ? '16px' : '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                color: '#f8fafc',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                fontSize: isMobile ? '11px' : '12px',
                zIndex: 9999,
                pointerEvents: 'none',
                padding: isMobile ? '14px' : '12px',
                width: `${tooltipWidth}px`,
                maxWidth: `${tooltipWidth}px`,
                fontFamily: 'system-ui, -apple-system, sans-serif',
                marginTop: '-8px'
              };

              return (
                <div key={`global-tooltip-${position}`} style={tooltipStyle}>
                  {/* Header */}
                  <div style={{
                    color: '#fbbf24',
                    fontWeight: '600',
                    fontSize: isMobile ? '10px' : '11px',
                    marginBottom: '8px',
                    textAlign: 'center',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase'
                  }}>
                    {hoveredYear} Season • {config.name}
                  </div>

                  {isHighlighted ? (
                    <div style={{ 
                      background: `linear-gradient(135deg, ${config.color}20, ${config.color}10)`,
                      borderRadius: '12px',
                      border: `1px solid ${config.color}40`,
                      padding: isMobile ? '10px' : '12px',
                      position: 'relative'
                    }}>
                      {/* Crown Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '-4px',
                        right: '8px',
                        backgroundColor: config.color,
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        fontSize: '8px',
                        fontWeight: '700',
                        textTransform: 'uppercase'
                      }}>
                        👑
                      </div>
                      
                      {/* Investment Amount */}
                      <div style={{ 
                        textAlign: 'center',
                        marginBottom: '8px',
                        marginTop: '4px'
                      }}>
                        <div style={{ 
                          fontSize: isMobile ? '20px' : '24px', 
                          fontWeight: '800',
                          color: config.color,
                          letterSpacing: '-0.5px'
                        }}>
                          ${value}
                        </div>
                        <div style={{ 
                          fontSize: '8px', 
                          color: '#94a3b8',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Investment
                        </div>
                      </div>
                      
                      {/* Player Name */}
                      <div style={{ 
                        fontSize: isMobile ? '12px' : '14px', 
                        fontWeight: '700',
                        color: '#f1f5f9',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        {hoveredDot.player}
                      </div>
                      
                      {/* Details */}
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: isMobile ? '9px' : '10px'
                      }}>
                        <span style={{ color: '#94a3b8' }}>Manager:</span>
                        <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{hoveredDot.manager}</span>
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: isMobile ? '9px' : '10px'
                      }}>
                        <span style={{ color: '#94a3b8' }}>Type:</span>
                        <span style={{
                          backgroundColor: hoveredDot.investment === 'Drafted' ? '#3b82f6' : '#f59e0b',
                          color: 'white',
                          padding: '1px 4px',
                          borderRadius: '4px',
                          fontSize: '8px',
                          fontWeight: '600'
                        }}>
                          {hoveredDot.investment}
                        </span>
                      </div>
                      
                      {/* Position Ring Banner */}
                      <div style={{
                        backgroundColor: `${config.color}20`,
                        border: `1px solid ${config.color}40`,
                        borderRadius: '6px',
                        padding: '4px',
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          fontSize: '8px', 
                          color: config.color,
                          fontWeight: '600'
                        }}>
                          {config.icon} {config.name.toUpperCase()} KING
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: isMobile ? '10px' : '12px' }}>
                      {/* Investment Amount */}
                      <div style={{ 
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        <div style={{ 
                          fontSize: isMobile ? '18px' : '20px', 
                          fontWeight: '800',
                          color: '#06b6d4',
                          letterSpacing: '-0.5px'
                        }}>
                          ${value}
                        </div>
                        <div style={{ 
                          fontSize: '8px', 
                          color: '#94a3b8',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          Investment
                        </div>
                      </div>
                      
                      {/* Player Name */}
                      <div style={{ 
                        fontSize: isMobile ? '11px' : '13px', 
                        fontWeight: '700',
                        color: '#f1f5f9',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        {hoveredDot.player}
                      </div>
                      
                      {/* Details */}
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '6px',
                        fontSize: isMobile ? '8px' : '9px'
                      }}>
                        <span style={{ color: '#94a3b8' }}>Manager:</span>
                        <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{hoveredDot.manager}</span>
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        fontSize: isMobile ? '8px' : '9px'
                      }}>
                        <span style={{ color: '#94a3b8' }}>Type:</span>
                        <span style={{
                          backgroundColor: hoveredDot.investment === 'Drafted' ? '#1e40af' : '#92400e',
                          color: 'white',
                          padding: '1px 4px',
                          borderRadius: '3px',
                          fontSize: '8px',
                          fontWeight: '600'
                        }}>
                          {hoveredDot.investment}
                        </span>
                      </div>
                      
                      {/* Comparison Section */}
                      <div style={{
                        backgroundColor: `${config.color}20`,
                        border: `1px solid ${config.color}40`,
                        borderRadius: '6px',
                        padding: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          fontSize: '8px', 
                          color: config.color,
                          fontWeight: '600',
                          lineHeight: '1.2'
                        }}>
                          📊 {data.playerName} {hoveredYear}: ${featuredAmount}
                          <br />
                          Career Total: ${data.totalCost}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionRingOfFame;